import React, { useState, useEffect } from 'react';
import { redirectsAPI, schemaAPI, seoAPI } from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { toast } from 'sonner';
import { ArrowRight, Plus, Trash2, Code2, ExternalLink, Search, BarChart3 } from 'lucide-react';

export default function AdminSEOAdvanced() {
  const [redirects, setRedirects] = useState([]);
  const [scores, setScores] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('scores');
  const [showAddRedirect, setShowAddRedirect] = useState(false);
  const [redirectForm, setRedirectForm] = useState({ from_path: '', to_path: '', type: '301' });
  const [jsonLdPreview, setJsonLdPreview] = useState(null);
  const [previewArticleId, setPreviewArticleId] = useState('');

  useEffect(() => { loadAll(); }, []);

  const loadAll = async () => {
    try {
      const [rRes, sRes] = await Promise.allSettled([
        redirectsAPI.list(),
        seoAPI.scores(),
      ]);
      if (rRes.status === 'fulfilled') setRedirects(rRes.value.data.redirects || []);
      if (sRes.status === 'fulfilled') setScores(sRes.value.data.scores || sRes.value.data.articles || []);
    } catch {}
    finally { setLoading(false); }
  };

  const addRedirect = async (e) => {
    e.preventDefault();
    try {
      await redirectsAPI.create(redirectForm);
      toast.success('Redirect created');
      setShowAddRedirect(false);
      setRedirectForm({ from_path: '', to_path: '', type: '301' });
      loadAll();
    } catch { toast.error('Failed to create redirect'); }
  };

  const deleteRedirect = async (id) => {
    try {
      await redirectsAPI.delete(id);
      toast.success('Redirect deleted');
      loadAll();
    } catch { toast.error('Failed to delete redirect'); }
  };

  const previewJsonLd = async () => {
    if (!previewArticleId) return;
    try {
      const res = await schemaAPI.article(previewArticleId);
      setJsonLdPreview(res.data);
    } catch { toast.error('Failed to load schema data'); }
  };

  const tabs = [
    { id: 'scores', label: 'SEO Scores', icon: BarChart3 },
    { id: 'redirects', label: 'Redirects', icon: ArrowRight },
    { id: 'schema', label: 'Schema.org JSON-LD', icon: Code2 },
  ];

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-[#1A1A1A]">Advanced SEO</h1>
        <p className="text-sm text-[#5B616E] mt-1">Manage SEO scores, redirects, and structured data</p>
      </div>

      <div className="flex gap-1 bg-[#F5F0E8] rounded-lg p-1">
        {tabs.map(t => (
          <button key={t.id} onClick={() => setActiveTab(t.id)} className={`flex-1 flex items-center justify-center gap-2 py-2 px-4 rounded-md text-sm font-medium transition-colors ${activeTab === t.id ? 'bg-white text-[#1A1A1A] shadow-sm' : 'text-[#5B616E]'}`}>
            <t.icon className="w-4 h-4" /> {t.label}
          </button>
        ))}
      </div>

      {activeTab === 'scores' && (
        <div className="bg-white rounded-xl border border-[#E5DFD5] overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-[#F5F0E8] border-b border-[#E5DFD5]">
              <tr>
                <th className="text-left px-4 py-3 font-medium text-[#5B616E]">Article</th>
                <th className="text-left px-4 py-3 font-medium text-[#5B616E]">Score</th>
                <th className="text-left px-4 py-3 font-medium text-[#5B616E]">Issues</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#E5DFD5]">
              {loading ? (
                <tr><td colSpan={3} className="px-4 py-8 text-center text-[#5B616E]">Loading...</td></tr>
              ) : scores.length === 0 ? (
                <tr><td colSpan={3} className="px-4 py-8 text-center text-[#5B616E]">No SEO data available.</td></tr>
              ) : scores.map((s, i) => (
                <tr key={i} className="hover:bg-[#FBF7EF]">
                  <td className="px-4 py-3 font-medium">{s.title || s.article_id}</td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <div className="w-24 h-2 rounded-full bg-gray-200 overflow-hidden">
                        <div className="h-full rounded-full" style={{ width: `${s.score || 0}%`, backgroundColor: (s.score || 0) > 70 ? '#1F7A5A' : (s.score || 0) > 40 ? '#B89B5E' : '#B3261E' }} />
                      </div>
                      <span className="text-xs font-medium">{s.score || 0}%</span>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    {(s.issues || []).map((issue, j) => (
                      <Badge key={j} variant="outline" className="text-[10px] mr-1 mb-1">{issue}</Badge>
                    ))}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {activeTab === 'redirects' && (
        <div className="space-y-4">
          <div className="flex justify-end">
            <Button onClick={() => setShowAddRedirect(true)} className="bg-[#1E5A8A]">
              <Plus className="w-4 h-4 mr-2" /> Add Redirect
            </Button>
          </div>

          {showAddRedirect && (
            <div className="bg-white rounded-xl border border-[#E5DFD5] p-6">
              <form onSubmit={addRedirect} className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div>
                  <label className="text-sm font-medium text-[#5B616E]">From Path</label>
                  <input value={redirectForm.from_path} onChange={e => setRedirectForm({ ...redirectForm, from_path: e.target.value })} required className="w-full mt-1 px-3 py-2 border border-[#E5DFD5] rounded-lg text-sm" placeholder="/old-path" />
                </div>
                <div>
                  <label className="text-sm font-medium text-[#5B616E]">To Path</label>
                  <input value={redirectForm.to_path} onChange={e => setRedirectForm({ ...redirectForm, to_path: e.target.value })} required className="w-full mt-1 px-3 py-2 border border-[#E5DFD5] rounded-lg text-sm" placeholder="/new-path" />
                </div>
                <div>
                  <label className="text-sm font-medium text-[#5B616E]">Type</label>
                  <select value={redirectForm.type} onChange={e => setRedirectForm({ ...redirectForm, type: e.target.value })} className="w-full mt-1 px-3 py-2 border border-[#E5DFD5] rounded-lg text-sm">
                    <option value="301">301 (Permanent)</option>
                    <option value="302">302 (Temporary)</option>
                  </select>
                </div>
                <div className="flex items-end gap-2">
                  <Button type="submit" className="bg-[#1E5A8A]">Add</Button>
                  <Button type="button" variant="outline" onClick={() => setShowAddRedirect(false)}>Cancel</Button>
                </div>
              </form>
            </div>
          )}

          <div className="bg-white rounded-xl border border-[#E5DFD5] overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-[#F5F0E8] border-b border-[#E5DFD5]">
                <tr>
                  <th className="text-left px-4 py-3 font-medium text-[#5B616E]">From</th>
                  <th className="text-left px-4 py-3 font-medium text-[#5B616E]">To</th>
                  <th className="text-left px-4 py-3 font-medium text-[#5B616E]">Type</th>
                  <th className="text-left px-4 py-3 font-medium text-[#5B616E]">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#E5DFD5]">
                {redirects.length === 0 ? (
                  <tr><td colSpan={4} className="px-4 py-8 text-center text-[#5B616E]">No redirects configured.</td></tr>
                ) : redirects.map(r => (
                  <tr key={r.id} className="hover:bg-[#FBF7EF]">
                    <td className="px-4 py-3 font-mono text-xs">{r.from_path}</td>
                    <td className="px-4 py-3 font-mono text-xs flex items-center gap-2"><ArrowRight className="w-3 h-3" />{r.to_path}</td>
                    <td className="px-4 py-3"><Badge variant="outline">{r.type}</Badge></td>
                    <td className="px-4 py-3">
                      <Button variant="ghost" size="sm" className="text-red-500 h-7 px-2" onClick={() => deleteRedirect(r.id)}>
                        <Trash2 className="w-3 h-3" />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {activeTab === 'schema' && (
        <div className="space-y-4">
          <div className="bg-white rounded-xl border border-[#E5DFD5] p-6">
            <h3 className="text-sm font-medium mb-3">Preview Schema.org JSON-LD for an article</h3>
            <div className="flex gap-2">
              <input value={previewArticleId} onChange={e => setPreviewArticleId(e.target.value)} placeholder="Enter article ID..." className="flex-1 px-3 py-2 border border-[#E5DFD5] rounded-lg text-sm" />
              <Button onClick={previewJsonLd} className="bg-[#1E5A8A]">
                <Code2 className="w-4 h-4 mr-2" /> Generate Preview
              </Button>
            </div>
          </div>

          {jsonLdPreview && (
            <div className="bg-white rounded-xl border border-[#E5DFD5] p-6">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-medium">JSON-LD Output</h3>
                <Button size="sm" variant="outline" onClick={() => navigator.clipboard.writeText(JSON.stringify(jsonLdPreview, null, 2)).then(() => toast.success('Copied to clipboard'))}>
                  Copy
                </Button>
              </div>
              <pre className="bg-[#0F1115] text-green-400 p-4 rounded-lg text-xs overflow-x-auto max-h-96">
                {JSON.stringify(jsonLdPreview, null, 2)}
              </pre>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
